import { useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
  ImportSession,
  isCurrentResponse,
  sessionFromDryRun,
  sessionMatchesResult,
} from "@/lib/importSession";


type Finding = {
  severity: "error" | "warning" | "info";
  code: string;
  sheet?: string;
  row?: number;
  field?: string;
  message: string;
};

type AthletePlan = {
  key: string;
  athlete_id: string | null;
  new_athlete_ref: string | null;
  display_name: string;
  published: boolean;
  classification: "create" | "update" | "unchanged" | "stale" | "invalid" | "skipped";
  changes: { field: string; before: string | null; after: string | null; cleared?: boolean }[];
  collections: { collection: string; before: number; after: number; changed: boolean }[];
  photos: { role: string; action: string; detail: string }[];
  images_required: string[];
  findings: Finding[];
};

type DryRun = {
  run_id: string | null;
  import_submission_key?: string | null;
  status: "ready" | "failed";
  workbook: {
    filename: string;
    workbook_instance_id: string;
    workbook_sha256: string;
    package_fingerprint: string;
    export_kind: string;
    exported_at: string;
    already_applied: boolean;
  };
  sheet_row_counts: Record<string, number>;
  totals: Record<string, number>;
  findings: Finding[];
  athletes: AthletePlan[];
  unused_images: string[];
  published_warning: string | null;
};

type ApplyResult = {
  outcome: "applied" | "already_applied" | "duplicate";
  run_id: string;
  counts?: Record<string, number>;
  published_changed?: string[];
  created?: { ref: string | null; athlete_id: string; slug: string | null; name: string | null }[];
  updated_ids?: string[];
  skipped_ids?: string[];
  message?: string;
};

const LABEL: Record<string, string> = {
  create: "New profile",
  update: "Will change",
  unchanged: "No change",
  stale: "Changed since download",
  invalid: "Cannot be used",
  skipped: "Skipped",
};

const TONE: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-900",
  update: "bg-sky-100 text-sky-900",
  unchanged: "bg-muted text-muted-foreground",
  stale: "bg-amber-100 text-amber-900",
  invalid: "bg-red-100 text-red-900",
  skipped: "bg-muted text-muted-foreground",
};

const fieldLabel = (f: string) => f.replace(/_/g, " ");

const downloadBase64 = (base64: string, filename: string) => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const readFunctionError = async (fnError: unknown): Promise<Record<string, unknown>> => {
  const ctx = (fnError as { context?: { text?: () => Promise<string> } }).context;
  const raw = ctx?.text ? await ctx.text() : (fnError as { message?: string }).message ?? "";
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return { message: raw || "The request could not be completed." };
  }
};

const ImportDryRun = () => {
  // The page is admin-gated, but the Apply control is additionally bound to a
  // verified admin role. The backend re-verifies independently on every call.
  const { isAdmin } = useAuth();
  const [workbook, setWorkbook] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DryRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staleChoice, setStaleChoice] = useState<Record<string, "skip" | "overwrite">>({});
  const [open, setOpen] = useState<string | null>(null);
  const [backupAt, setBackupAt] = useState<string | null>(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedResult, setAppliedResult] = useState<ApplyResult | null>(null);
  // "rolled_back" = the database confirmed nothing was written.
  // "unresolved"  = the outcome could not be established; never claim either way.
  const [failureKind, setFailureKind] = useState<"rolled_back" | "unresolved" | null>(null);
  // Reference identifiers issued by the dry run — never authorization.
  const [session, setSession] = useState<ImportSession | null>(null);
  const wbInput = useRef<HTMLInputElement>(null);
  const imgInput = useRef<HTMLInputElement>(null);
  const requestKey = useRef<string | null>(null);
  // Newest dry-run request wins: a slower earlier answer is discarded.
  const latestSeq = useRef(0);

  const errors = useMemo(() => (result?.findings ?? []).filter((f) => f.severity === "error"), [result]);
  const warnings = useMemo(() => (result?.findings ?? []).filter((f) => f.severity === "warning"), [result]);

  // Athletes that would actually be written, after the Skip/Overwrite choices.
  const mutating = useMemo(
    () =>
      (result?.athletes ?? []).filter((a) =>
        a.classification === "create" || a.classification === "update" ||
        (a.classification === "stale" && staleChoice[a.key] === "overwrite")
      ),
    [result, staleChoice],
  );
  const publishedAffected = useMemo(
    () => mutating.filter((a) => a.published && a.athlete_id),
    [mutating],
  );
  const staleUndecided = useMemo(
    () => (result?.athletes ?? []).filter((a) => a.classification === "stale" && !staleChoice[a.key]),
    [result, staleChoice],
  );

  const canApply = Boolean(
    isAdmin &&
      result &&
      result.status === "ready" &&
      sessionMatchesResult(session, result) &&
      workbook &&
      mutating.length > 0 &&
      staleUndecided.length === 0 &&
      backupAt &&
      !appliedResult &&
      failureKind !== "unresolved" &&
      !applying,
  );

  const resetConfirmation = () => {
    setBackupAt(null);
    setConfirmOpen(false);
    setConfirmChecked(false);
    setSession(null);
    setStaleChoice({});
    requestKey.current = null;
  };

  /** Any change to the chosen files invalidates the previous check and confirmation. */
  const onSelectionChanged = () => {
    latestSeq.current += 1;
    setResult(null);
    setError(null);
    setAppliedResult(null);
    setFailureKind(null);
    setOpen(null);
    resetConfirmation();
  };

  const reset = () => {
    setWorkbook(null);
    setImages([]);
    setApplying(false);
    onSelectionChanged();
    if (wbInput.current) wbInput.current.value = "";
    if (imgInput.current) imgInput.current.value = "";
  };

  const downloadCurrentData = async (label: string) => {
    setBackupBusy(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke<{ xlsx_base64: string; filename: string }>(
        "generate-workbook",
        { body: { kind: "current_data" } },
      );
      if (fnError || !data?.xlsx_base64) throw fnError ?? new Error("no workbook");
      downloadBase64(data.xlsx_base64, data.filename);
      toast.success(label);
      return true;
    } catch {
      toast.error("The workbook could not be prepared.");
      return false;
    } finally {
      setBackupBusy(false);
    }
  };

  const run = async () => {
    if (!workbook) return;
    const seq = ++latestSeq.current;
    setBusy(true);
    setError(null);
    setResult(null);
    setAppliedResult(null);
    setFailureKind(null);
    resetConfirmation();
    try {
      const form = new FormData();
      form.append("workbook", workbook);
      for (const f of images) form.append("images", f);
      const { data, error: fnError } = await supabase.functions.invoke("import-dry-run", { body: form });
      // Discard anything that is no longer the newest request or file selection.
      if (!isCurrentResponse(seq, latestSeq.current)) return;
      if (fnError) {
        const parsed = await readFunctionError(fnError);
        setError((parsed.message as string) ?? (parsed.error as string) ?? "The check could not be completed.");
        return;
      }
      const d = data as DryRun;
      setResult(d);
      setSession(sessionFromDryRun(d));
      if (d.status === "ready") toast.success("Check complete — nothing has been changed");
      else toast.error("The spreadsheet has problems that must be fixed first");
    } catch {
      if (!isCurrentResponse(seq, latestSeq.current)) return;
      setError("The check could not be completed.");
    } finally {
      if (isCurrentResponse(seq, latestSeq.current)) setBusy(false);
    }
  };

  const submitApply = async () => {
    // Repeated clicks can never start a second import: the button is disabled
    // while applying, this guard rejects re-entry, and the same client request
    // key is reused so the backend replays the first result instead of redoing it.
    if (applying || appliedResult) return;
    if (!canApply && !confirmOpen) return;
    if (!workbook || !result || !session || !sessionMatchesResult(session, result)) return;
    setApplying(true);
    setError(null);
    requestKey.current = requestKey.current ?? crypto.randomUUID();
    try {
      const form = new FormData();
      form.append("workbook", workbook);
      for (const f of images) form.append("images", f);
      form.append(
        "payload",
        JSON.stringify({
          run_id: session.runId,
          import_submission_key: session.submissionKey,
          package_fingerprint: session.packageFingerprint,
          client_request_key: requestKey.current,
          stale_decisions: staleChoice,
          confirmed_published_ids: publishedAffected.map((a) => a.athlete_id),
          backup_taken_at: backupAt,
        }),
      );
      const { data, error: fnError } = await supabase.functions.invoke("import-apply", { body: form });
      if (fnError) {
        const parsed = await readFunctionError(fnError);
        if (parsed.updated_result) {
          const u = parsed.updated_result as Partial<DryRun>;
          setResult((prev) => (prev ? { ...prev, ...u, status: "ready" } as DryRun : prev));
          setStaleChoice({});
          resetConfirmation();
        }
        if (parsed.error === "outcome_unresolved") {
          setFailureKind("unresolved");
          setConfirmOpen(false);
          setError(
            "The result of this import could not be confirmed. It may or may not have been " +
              "applied. Do not press Apply again — check the athlete list first, and download a " +
              "fresh current-data workbook to see the real state.",
          );
          toast.error("Result could not be confirmed");
          return;
        }
        setFailureKind("rolled_back");
        setError((parsed.message as string) ?? "The import was not applied. Nothing was changed.");
        toast.error("Nothing was changed");
        return;
      }
      const applied = data as ApplyResult;
      setAppliedResult(applied);
      setFailureKind(null);
      setConfirmOpen(false);
      toast.success(
        applied.outcome === "applied" ? "Import applied" : "This import had already been applied",
      );
    } catch {
      setFailureKind("unresolved");
      setError(
        "The import did not return a result, so it is not known whether it was applied. " +
          "Pressing Apply again is safe — the same import can never be applied twice — but " +
          "check the athlete list or a fresh current-data workbook first.",
      );
    } finally {
      setApplying(false);
    }
  };

  const startApply = () => {
    if (publishedAffected.length > 0) {
      setConfirmChecked(false);
      setConfirmOpen(true);
    } else {
      void submitApply();
    }
  };

  return (
    <section className="mt-12">
      <h2 className="font-display text-3xl">Check and apply a filled-in spreadsheet</h2>
      <p className="mt-2 text-muted-foreground">
        Upload a workbook to see exactly what it would do. Nothing is changed until you download a
        backup and confirm.
      </p>

      <div className="mt-6 bg-card rounded-[1.5rem] shadow-card p-6 grid gap-5">
        <div>
          <label className="text-sm font-semibold" htmlFor="wb-file">Spreadsheet (.xlsx)</label>
          <input
            id="wb-file"
            ref={wbInput}
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              setWorkbook(e.target.files?.[0] ?? null);
              onSelectionChanged();
            }}
            className="mt-2 block w-full text-sm file:mr-4 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-secondary-foreground"
          />
        </div>
        <div>
          <label className="text-sm font-semibold" htmlFor="img-files">New photos (optional)</label>
          <input
            id="img-files"
            ref={imgInput}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            multiple
            onChange={(e) => {
              setImages([...(e.target.files ?? [])]);
              onSelectionChanged();
            }}
            className="mt-2 block w-full text-sm file:mr-4 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-secondary-foreground"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Only needed for photos you named in the spreadsheet. {images.length} selected.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={run}
            disabled={!workbook || busy || applying}
            className="px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
          >
            {busy ? "Checking…" : "Check spreadsheet"}
          </button>
          <button
            onClick={reset}
            disabled={busy || applying}
            className="px-6 py-3 rounded-full border border-border font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {result ? "Start over" : "Cancel"}
          </button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className={`mt-6 rounded-[1.5rem] border p-6 ${
            failureKind === "unresolved"
              ? "border-amber-400 bg-amber-50 text-amber-900"
              : "border-red-300 bg-red-50 text-red-900"
          }`}
        >
          <h3 className="font-semibold">
            {failureKind === "unresolved"
              ? "The result could not be confirmed"
              : "Nothing was changed"}
          </h3>
          <p className="mt-1 text-sm">{error}</p>
        </div>
      )}

      {appliedResult && (
        <div className="mt-8 rounded-[1.5rem] border-2 border-emerald-400 bg-emerald-50 p-6 text-emerald-950">
          <h3 className="font-display text-2xl">
            {appliedResult.outcome === "applied" ? "Import applied" : "Already applied"}
          </h3>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4 text-sm">
            {[
              ["New profiles", appliedResult.counts?.created],
              ["Profiles changed", appliedResult.counts?.updated],
              ["Skipped", appliedResult.counts?.skipped],
              ["Already up to date", appliedResult.counts?.unchanged],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <dt className="opacity-80">{label}</dt>
                <dd className="text-2xl font-display">{Number(value ?? 0)}</dd>
              </div>
            ))}
          </dl>
          {appliedResult.outcome !== "applied" && (
            <p className="mt-2 text-sm">
              This exact spreadsheet had already been applied, so nothing was repeated. The figures
              below are the results of that original import.
            </p>
          )}
          <p className="mt-3 text-sm">
            Live profiles changed: {appliedResult.published_changed?.length ?? 0}. New profiles are
            created hidden from the public — publishing each one stays a separate, manual step.
          </p>
          {(appliedResult.created ?? []).length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="opacity-80">
                    <th className="py-1 pr-4 font-medium">Spreadsheet reference</th>
                    <th className="py-1 pr-4 font-medium">Name</th>
                    <th className="py-1 pr-4 font-medium">Web address</th>
                    <th className="py-1 font-medium">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {(appliedResult.created ?? []).map((c) => (
                    <tr key={c.athlete_id} className="border-t border-emerald-200 align-top">
                      <td className="py-1 pr-4 font-mono break-all">{c.ref}</td>
                      <td className="py-1 pr-4">{c.name}</td>
                      <td className="py-1 pr-4 font-mono break-all">{c.slug}</td>
                      <td className="py-1">
                        <a className="underline" href={`/admin/athletes#${c.athlete_id}`}>Admin profile</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={() => downloadCurrentData("Updated workbook downloaded")}
              disabled={backupBusy}
              className="px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
            >
              {backupBusy ? "Preparing…" : "Download the updated current-data workbook"}
            </button>
            <button
              onClick={reset}
              className="px-6 py-3 rounded-full border border-emerald-400 font-semibold hover:bg-emerald-100 transition-colors"
            >
              Start over
            </button>
          </div>
        </div>
      )}

      {result && !appliedResult && (
        <div className="mt-8 grid gap-6">
          {result.published_warning && (
            <div role="alert" className="rounded-[1.5rem] border-2 border-red-400 bg-red-50 p-6 text-red-900">
              <h3 className="font-semibold">Live profiles would be affected</h3>
              <p className="mt-1 text-sm">{result.published_warning}</p>
            </div>
          )}

          <div className="bg-card rounded-[1.5rem] shadow-card p-6">
            <h3 className="font-display text-2xl">Summary</h3>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 text-sm">
              {[
                ["New profiles", result.totals.created],
                ["Profiles that would change", result.totals.updated],
                ["Already up to date", result.totals.unchanged],
                ["Changed since download", result.totals.stale],
                ["Rows with problems", result.totals.invalid],
                ["Live profiles affected", result.totals.published_touched],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="text-2xl font-display">{value ?? 0}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground font-mono break-all">
              {result.workbook.filename} · identity {result.workbook.workbook_instance_id}
            </p>
            {result.workbook.already_applied && (
              <p className="mt-2 text-sm text-amber-800">
                This spreadsheet has already been used once before, so it cannot be applied again.
              </p>
            )}
          </div>

          {(errors.length > 0 || warnings.length > 0) && (
            <div className="bg-card rounded-[1.5rem] shadow-card p-6">
              <h3 className="font-display text-2xl">Problems found</h3>
              <ul className="mt-4 grid gap-2 text-sm">
                {[...errors, ...warnings].map((f, i) => (
                  <li key={i} className="flex gap-3">
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        f.severity === "error" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900"
                      }`}
                    >
                      {f.severity === "error" ? "Must fix" : "Check"}
                    </span>
                    <span>
                      {f.sheet && <strong>{f.sheet}</strong>}
                      {f.row ? ` row ${f.row}` : ""}
                      {f.field ? ` · ${fieldLabel(f.field)}` : ""}
                      {f.sheet ? " — " : ""}
                      {f.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-card rounded-[1.5rem] shadow-card p-6">
            <h3 className="font-display text-2xl">Athlete by athlete</h3>
            <div className="mt-4 grid gap-3">
              {result.athletes.map((a) => (
                <div key={a.key} className="rounded-2xl border border-border">
                  <button
                    onClick={() => setOpen(open === a.key ? null : a.key)}
                    className="w-full flex flex-wrap items-center gap-3 p-4 text-left"
                    aria-expanded={open === a.key}
                  >
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TONE[a.classification]}`}>
                      {LABEL[a.classification]}
                    </span>
                    <span className="font-semibold">{a.display_name}</span>
                    {a.published && (
                      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs">Live</span>
                    )}
                    <span className="ml-auto text-sm text-muted-foreground">
                      {a.changes.length} field{a.changes.length === 1 ? "" : "s"}
                    </span>
                  </button>
                  {open === a.key && (
                    <div className="border-t border-border p-4 grid gap-4 text-sm">
                      {a.findings.length > 0 && (
                        <ul className="grid gap-1">
                          {a.findings.map((f, i) => (
                            <li key={i} className={f.severity === "error" ? "text-red-800" : "text-amber-800"}>
                              {f.sheet ? `${f.sheet}${f.row ? ` row ${f.row}` : ""} — ` : ""}{f.message}
                            </li>
                          ))}
                        </ul>
                      )}
                      {a.changes.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="text-muted-foreground">
                                <th className="py-1 pr-4 font-medium">Field</th>
                                <th className="py-1 pr-4 font-medium">Now</th>
                                <th className="py-1 font-medium">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {a.changes.map((c) => (
                                <tr key={c.field} className="border-t border-border/60 align-top">
                                  <td className="py-1 pr-4">{fieldLabel(c.field)}</td>
                                  <td className="py-1 pr-4 text-muted-foreground break-words">{c.before ?? "—"}</td>
                                  <td className="py-1 break-words">{c.cleared ? "(emptied)" : c.after ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {a.collections.some((c) => c.changed) && (
                        <ul className="grid gap-1">
                          {a.collections.filter((c) => c.changed).map((c) => (
                            <li key={c.collection}>
                              {c.collection}: {c.before} → {c.after} (the whole list is replaced)
                            </li>
                          ))}
                        </ul>
                      )}
                      {a.photos.length > 0 && (
                        <ul className="grid gap-1">
                          {a.photos.map((p, i) => (
                            <li key={i}>
                              {p.role.replace("_", " ")}: {p.detail}
                            </li>
                          ))}
                        </ul>
                      )}
                      {a.classification === "stale" && (
                        <div className="rounded-xl bg-amber-50 p-3">
                          <p className="text-amber-900">
                            This row cannot be shown to match the profile as it stands now (it was
                            edited after download, or its change-detection stamps are missing or do
                            not match). Choose what should happen before applying.
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {(["skip", "overwrite"] as const).map((c) => (
                              <button
                                key={c}
                                onClick={() => {
                                  setStaleChoice({ ...staleChoice, [a.key]: c });
                                  setConfirmChecked(false);
                                }}
                                className={`rounded-full px-4 py-1.5 text-xs font-semibold ${
                                  staleChoice[a.key] === c
                                    ? "bg-secondary text-secondary-foreground"
                                    : "border border-border"
                                }`}
                              >
                                {c === "skip" ? "Skip this athlete" : "Overwrite anyway"}
                              </button>
                            ))}
                            {!staleChoice[a.key] && (
                              <span className="text-xs font-semibold text-amber-900">
                                Choice needed
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {result.athletes.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  This spreadsheet contains no athlete rows, so there is nothing to do.
                </p>
              )}
            </div>
          </div>

          {/* ---------------- apply ---------------- */}
          <div className="bg-card rounded-[1.5rem] shadow-card p-6">
            <h3 className="font-display text-2xl">Apply this import</h3>
            {result.status !== "ready" || mutating.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {result.status !== "ready"
                  ? "Fix the problems above, then check the spreadsheet again."
                  : "This spreadsheet would change nothing, so there is nothing to apply."}
              </p>
            ) : (
              <>
                <ol className="mt-4 grid gap-3 text-sm">
                  <li className="flex flex-wrap items-center gap-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${backupAt ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                      {backupAt ? "Done" : "Required"}
                    </span>
                    <span>Download a backup of every profile as it stands right now.</span>
                    <button
                      onClick={async () => {
                        const ok = await downloadCurrentData("Backup downloaded");
                        if (ok) setBackupAt(new Date().toISOString());
                      }}
                      disabled={backupBusy || applying}
                      className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                    >
                      {backupBusy ? "Preparing…" : backupAt ? "Download again" : "Download pre-import backup"}
                    </button>
                  </li>
                  <li className="flex flex-wrap items-center gap-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${staleUndecided.length === 0 ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                      {staleUndecided.length === 0 ? "Done" : "Required"}
                    </span>
                    <span>
                      {staleUndecided.length === 0
                        ? "Every profile that changed since download has a choice."
                        : `${staleUndecided.length} profile(s) changed since download and still need Skip or Overwrite.`}
                    </span>
                  </li>
                </ol>
                <p className="mt-4 text-sm">
                  This will create {mutating.filter((a) => a.classification === "create").length} new
                  profile(s) and change {mutating.filter((a) => a.classification !== "create").length}{" "}
                  existing profile(s). {publishedAffected.length} of them are live to the public.
                </p>
                {!isAdmin && (
                  <p className="mt-4 text-sm text-amber-800">
                    Only a verified administrator can apply an import.
                  </p>
                )}
                <button
                  onClick={startApply}
                  disabled={!canApply}
                  className="mt-5 px-6 py-3 rounded-full bg-red-700 text-white font-semibold hover:bg-red-800 transition-colors disabled:opacity-50"
                >
                  {applying ? "Applying…" : "Apply this import"}
                </button>
                {applying && (
                  <p className="mt-3 text-sm text-muted-foreground" role="status">
                    Applying. This can take up to a minute for a large batch — please do not close
                    this page. If the answer never arrives, pressing Apply again is safe: the same
                    import can never be applied twice.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="apply-confirm-title">
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-[1.5rem] border-2 border-red-500 bg-card p-6">
            <h3 id="apply-confirm-title" className="font-display text-2xl text-red-800">
              These live profiles change for the public
            </h3>
            <p className="mt-2 text-sm">
              The profiles below are visible on the website right now. Once you apply this import,
              the changes are public immediately.
            </p>
            <ul className="mt-4 grid gap-1 text-sm">
              {publishedAffected.map((a) => (
                <li key={a.key} className="font-semibold">{a.display_name}</li>
              ))}
            </ul>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              {[
                ["New", mutating.filter((a) => a.classification === "create").length],
                ["Changed", mutating.filter((a) => a.classification !== "create").length],
                ["Skipped", Object.values(staleChoice).filter((c) => c === "skip").length],
                ["Live affected", publishedAffected.length],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="text-2xl font-display">{Number(value)}</dd>
                </div>
              ))}
            </dl>
            <label className="mt-5 flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-1"
              />
              <span>
                I understand that {publishedAffected.length} live profile(s) will change for the
                public straight away.
              </span>
            </label>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => void submitApply()}
                disabled={!confirmChecked || applying}
                className="px-6 py-3 rounded-full bg-red-700 text-white font-semibold hover:bg-red-800 disabled:opacity-50"
              >
                {applying ? "Applying…" : "Yes, apply now"}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={applying}
                className="px-6 py-3 rounded-full border border-border font-semibold hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ImportDryRun;
